'use client';

import { useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { logout, restoreSession } from '@/store/slices/authSlice';
import apiClient from '@/shared/services/apiClient';
import { ChevronDown, User, Bell, Menu, X, LayoutDashboard } from 'lucide-react';
import NotificationDropdown from '@/components/notifications/NotificationDropdown';

export default function Navbar() {
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const [mounted, setMounted] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);

    // Check localStorage tokens and validate with backend
    const validateAndRestoreSession = async () => {
      const accessToken = localStorage.getItem('accessToken');
      const storedUser = localStorage.getItem('user');

      if (accessToken && storedUser && !isAuthenticated) {
        try {
          // Validate token by making a test API call
          await apiClient.get('/auth/me');
          // Token is valid, restore session
          const parsedUser = JSON.parse(storedUser);
          dispatch(restoreSession({
            user: parsedUser,
            accessToken,
            isAuthenticated: true
          }));
        } catch (_error: unknown) {
          // Clear invalid data on error
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
        }
      }
    };

    validateAndRestoreSession();
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setIsMobileMenuOpen(false);
    await dispatch(logout());
    router.push('/');
  };

  if (!mounted) {
    return (
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-200/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <Link href="/" className="text-xl font-bold text-blue-600">
              Refleco
            </Link>
          </div>
        </div>
      </nav>
    );
  }

  /** Links available to ALL authenticated users */
  const commonLinks = [
    { href: '/onboarding', label: '진단하기' },
    { href: '/checkin', label: '체크인' },
    { href: '/goals', label: '목표' },
    { href: '/my-tests', label: '내 결과' },
  ];

  /** Role-specific links */
  const roleLinks: { href: string; label: string }[] = [];
  if (user?.account_type === 'super_admin') {
    roleLinks.push({ href: '/admin', label: '관리자' });
  }
  if (user?.account_type === 'group_manager') {
    roleLinks.push({ href: '/group-manager', label: '그룹관리' });
  }
  if (user?.account_type === 'student') {
    roleLinks.push({ href: '/student/join-group', label: '그룹 가입' });
  }
  if (user?.account_type === 'parent') {
    roleLinks.push({ href: '/parent', label: '자녀관리' });
  }

  const allNavLinks = [...commonLinks, ...roleLinks];

  // 온보딩/디스커버리 플로우 안에서는 Navbar 간소화
  const isOnboardingFlow = pathname === '/onboarding' || pathname === '/discovery';

  return (
    <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-200/50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          {/* 로고 + 대시보드 */}
          <div className="flex items-center gap-2">
            <Link href="/" className="text-xl font-bold text-blue-600">
              Refleco
            </Link>
            {isAuthenticated && user && (
              <Link
                href="/dashboard"
                className="flex items-center gap-1 px-2 py-1.5 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="대시보드"
              >
                <LayoutDashboard className="w-4 h-4" />
                <span className="hidden sm:inline text-xs font-medium">대시보드</span>
              </Link>
            )}
          </div>

          {/* ========== Desktop menu ========== */}
          <div className="hidden md:flex items-center space-x-6">
            {isAuthenticated && user ? (
              <>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">
                    {user.full_name || user.username}님
                  </span>

                  {/* Common + role-specific links */}
                  {allNavLinks.map((link) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      className="text-sm text-gray-700 hover:text-blue-600"
                    >
                      {link.label}
                    </Link>
                  ))}

                  {/* 알림 드롭다운 */}
                  <NotificationDropdown />

                  {/* 내 정보 드롭다운 */}
                  <div className="relative" ref={profileRef}>
                    <button
                      onClick={() => setIsProfileOpen(!isProfileOpen)}
                      className="flex items-center text-sm text-gray-700 hover:text-blue-600"
                      aria-expanded={isProfileOpen}
                      aria-label="사용자 메뉴"
                      aria-haspopup="true"
                    >
                      <User className="w-4 h-4 mr-1" />
                      내 정보
                      <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isProfileOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white/90 backdrop-blur-md rounded-xl shadow-lg py-1 z-50 border border-gray-200/50" role="menu">
                        <Link
                          href="/profile"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsProfileOpen(false)}
                          role="menuitem"
                        >
                          프로필 관리
                        </Link>

                        {user.account_type === 'student' && (
                          <Link
                            href="/student/parent-connection"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setIsProfileOpen(false)}
                            role="menuitem"
                          >
                            <div className="flex items-center justify-between">
                              <span>부모 연결 요청</span>
                              <Bell className="w-4 h-4 text-blue-600" />
                            </div>
                          </Link>
                        )}

                        {user.account_type === 'student' && (
                          <Link
                            href="/student/group-requests"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setIsProfileOpen(false)}
                            role="menuitem"
                          >
                            <div className="flex items-center justify-between">
                              <span>그룹 가입 요청</span>
                              <Bell className="w-4 h-4 text-blue-600" />
                            </div>
                          </Link>
                        )}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={handleLogout}
                    className="text-sm text-gray-700 hover:text-blue-600"
                  >
                    로그아웃
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* 비로그인 사용자 메뉴 — 온보딩/디스커버리 플로우에서는 로그인만 표시 */}
                <Link
                  href="/auth/login"
                  className="text-sm text-gray-700 hover:text-blue-600"
                >
                  로그인
                </Link>
                {!isOnboardingFlow && (
                  <Link
                    href="/auth/register"
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                  >
                    회원가입
                  </Link>
                )}
              </>
            )}
          </div>

          {/* ========== Mobile hamburger button ========== */}
          <button
            className="md:hidden p-2 text-gray-700 hover:text-blue-600"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? '메뉴 닫기' : '메뉴 열기'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* ========== Mobile menu panel (minimal — bottom tab handles main nav) ========== */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200/50 bg-white/90 backdrop-blur-md" role="menu" aria-label="모바일 메뉴">
          <div className="container mx-auto px-4 py-3 space-y-2">
            {isAuthenticated && user ? (
              <>
                <div className="px-2 py-2 text-sm font-medium text-gray-500 border-b border-gray-100">
                  {user.full_name || user.username}님
                </div>

                <Link
                  href="/dashboard"
                  className="flex items-center gap-2 px-2 py-2 text-sm text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded"
                  onClick={() => setIsMobileMenuOpen(false)}
                  role="menuitem"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  대시보드
                </Link>

                {/* Role-specific links (not in bottom tab) */}
                {roleLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="block px-2 py-2 text-sm text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded"
                    onClick={() => setIsMobileMenuOpen(false)}
                    role="menuitem"
                  >
                    {link.label}
                  </Link>
                ))}

                {/* Extra links */}
                <Link
                  href="/onboarding"
                  className="block px-2 py-2 text-sm text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded"
                  onClick={() => setIsMobileMenuOpen(false)}
                  role="menuitem"
                >
                  진단하기
                </Link>
                <Link
                  href="/goals"
                  className="block px-2 py-2 text-sm text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded"
                  onClick={() => setIsMobileMenuOpen(false)}
                  role="menuitem"
                >
                  목표
                </Link>
                <Link
                  href="/my-tests"
                  className="block px-2 py-2 text-sm text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded"
                  onClick={() => setIsMobileMenuOpen(false)}
                  role="menuitem"
                >
                  내 결과
                </Link>

                <div className="border-t border-gray-100 pt-2 mt-2">
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-2 py-2 text-sm text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded"
                    role="menuitem"
                  >
                    로그아웃
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link
                  href="/auth/login"
                  className="block px-2 py-2 text-sm text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded"
                  onClick={() => setIsMobileMenuOpen(false)}
                  role="menuitem"
                >
                  로그인
                </Link>
                {!isOnboardingFlow && (
                  <Link
                    href="/auth/register"
                    className="block px-2 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded"
                    onClick={() => setIsMobileMenuOpen(false)}
                    role="menuitem"
                  >
                    회원가입
                  </Link>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
